import { useState, useEffect, useRef, useCallback } from "react";
import { useApi } from "./useApi";

const STORAGE_KEY = "AFFEKTRA_PREFS";

export const ALL_TABS = [
  "prepare",
  "canvas",
  "assistant",
  "journey",
  "goals",
  "calendar",
  "histories",
  "assessments",
  "prompts",
  "library",
] as const;

export type TabId = (typeof ALL_TABS)[number];

/** The summarize prompt the session summarizer runs (picked in the prompt
 * library). note_format marks a structured clinical note (SOAP/DAP/BIRP);
 * absent → freeform summary. null/undefined → legacy default (backend picks
 * the freeform seed prompt). */
export interface SummarizePromptSelection {
  id: string;
  note_format?: "soap" | "dap" | "birp";
}

/** Progress note drafted automatically after each session
 * (POST /sessions/{id}/complete). Unset = not configured yet. */
export type AutoNoteFormat = "soap" | "dap" | "birp" | "off";

export interface Preferences {
  visibleTabs: TabId[];
  summarizePrompt?: SummarizePromptSelection | null;
  autoNoteFormat?: AutoNoteFormat;
}

const DEFAULT_PREFS: Preferences = {
  visibleTabs: [...ALL_TABS],
};

function loadCached(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Preferences;
  } catch {}
  return DEFAULT_PREFS;
}

function saveCached(prefs: Preferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {}
}

export function usePreferences() {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [prefs, setPrefs] = useState<Preferences>(loadCached);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef<number | null>(null);
  const skipNextSync = useRef(true); // skip the initial effect

  // Fetch from server once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRef.current.get<{ ok: boolean; preferences: Preferences }>(
          "/preferences"
        );
        if (!cancelled && res.preferences) {
          // Auto-show any tabs added since prefs were last saved
          const saved = res.preferences;
          const savedSet = new Set(saved.visibleTabs as string[]);
          const newTabs = ALL_TABS.filter((t) => !savedSet.has(t));
          const merged: Preferences = newTabs.length > 0
            ? { ...saved, visibleTabs: [...saved.visibleTabs, ...newTabs] }
            : saved;
          setPrefs(merged);
          saveCached(merged);
        }
      } catch {
        // use cached/default
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced PUT to server on changes
  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    saveCached(prefs);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        await apiRef.current.put("/preferences", prefs);
      } catch {}
    }, 600);
    // Clear pending timer on unmount to avoid state updates after unmount
    return () => {
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [prefs]);

  const updatePrefs = useCallback((update: Partial<Preferences>) => {
    setPrefs((prev) => ({ ...prev, ...update }));
  }, []);

  const setVisibleTabs = useCallback((tabs: TabId[]) => {
    // Ensure at least 2 tabs remain visible
    if (tabs.length < 2) return;
    setPrefs((prev) => ({ ...prev, visibleTabs: tabs }));
  }, []);

  const toggleTab = useCallback((tabId: TabId) => {
    setPrefs((prev) => {
      const current = prev.visibleTabs;
      const isVisible = current.includes(tabId);
      if (isVisible) {
        // Don't allow hiding if only 2 left
        if (current.length <= 2) return prev;
        return { ...prev, visibleTabs: current.filter((t) => t !== tabId) };
      } else {
        return { ...prev, visibleTabs: [...current, tabId] };
      }
    });
  }, []);

  const hideTab = useCallback((tabId: TabId) => {
    setPrefs((prev) => {
      if (prev.visibleTabs.length <= 2) return prev;
      return {
        ...prev,
        visibleTabs: prev.visibleTabs.filter((t) => t !== tabId),
      };
    });
  }, []);

  const setSummarizePrompt = useCallback((sel: SummarizePromptSelection | null) => {
    setPrefs((prev) => ({ ...prev, summarizePrompt: sel }));
  }, []);

  return { prefs, loading, updatePrefs, setVisibleTabs, toggleTab, hideTab, setSummarizePrompt };
}
